import { useState } from 'react';
import { Button, Card, CardContent } from '@exam-prep/ui';
import { useLoad } from '../../lib/useLoad';
import { api, ApiError } from '../../lib/api';
import { ErrorBox, Loading } from '../../components/Feedback';
import type { AttemptView } from '../../lib/api-types';
import type { Props, Ticks } from './model/grand-mock-rules';
import type { GmRules, GmLearner } from './model/types';
import { useOnline } from './hooks/useOnline';
import { Panel, GmHeader, OfflineNote } from './ui/GrandMockParts';
import { GM, RULES } from './model/copy';
import { deviceHint } from './model/device';
import { friendlyError } from './model/flow';

/**
 * Instructions plus the explicit agreement. Tapping the button saves the agreement on the server
 * and, when the exam is open, starts the one attempt. Nothing starts without every box ticked.
 */
const goLabel = (busy: 'agree' | 'start' | null, canStart: boolean): string =>
  busy === 'start'
    ? RULES.starting
    : busy === 'agree'
      ? RULES.saving
      : canStart
        ? RULES.agree
        : RULES.agreeOnly;

/** Busy while agreeing or starting, with the step's label on the full-screen loader. */
const pendingProps = (busy: 'agree' | 'start' | null, canStart: boolean) => ({
  pending: busy !== null,
  pendingOverlay: goLabel(busy, canStart),
});

export function GrandMockRules({ gmId, onBack, onStarted }: Props) {
  const rules = useLoad(() => api<GmRules>('GET', '/app/grand-mock/rules'), [gmId]);
  const state = useLoad(() => api<GmLearner>('GET', '/app/grand-mock'), [gmId]);
  const online = useOnline();
  const [ticks, setTicks] = useState<Ticks>({ age: false, india: false, rules: false });
  const [busy, setBusy] = useState<'agree' | 'start' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [agreedAhead, setAgreedAhead] = useState(false);

  if (rules.error) return <ErrorBox error={rules.error} retry={rules.reload} />;
  if (state.error && !state.data) return <ErrorBox error={state.error} retry={state.reload} />;
  if (!rules.data || !state.data) return <Loading />;
  const gm = state.data.grandMock;
  const me = state.data.me;
  if (!gm || gm.id !== gmId)
    return (
      <div className="space-y-4 p-4">
        <Panel>
          <p role="status">{GM.noneYet}</p>
        </Panel>
        <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
          {GM.back}
        </Button>
      </div>
    );

  const canStart = !!me?.canStart;
  const allTicked = rules.data.agreements.every((a) => ticks[a.key]);
  const disabled = !allTicked || !!busy || !online || !me?.eligibility.eligible;

  const go = async () => {
    if (!rules.data || disabled) return;
    setProblem(null);
    setBusy('agree');
    try {
      await api('POST', `/app/grand-mock/${gmId}/agree`, {
        age: ticks.age,
        india: ticks.india,
        rules: ticks.rules,
        rulesVersion: rules.data.version,
      });
      if (!canStart) {
        setAgreedAhead(true);
        return;
      }
      setBusy('start');
      const device = await deviceHint();
      const v = await api<AttemptView>(
        'POST',
        `/app/grand-mock/${gmId}/attempts`,
        device ? { device } : {},
      );
      onStarted(v.attemptId);
    } catch (e) {
      if (e instanceof ApiError) {
        setProblem(friendlyError(e.code, e.message));
        if (e.code === 'grand_mock.rules_changed') rules.reload();
        if (e.code === 'mock.already_attempted') {
          // The attempt exists: go back to the entry screen, which resumes or shows the state.
          onBack();
        }
      } else setProblem(GM.loadError);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4 p-4 pb-8">
      <Card>
        <GmHeader gm={gm} />
        <CardContent className="space-y-3 text-sm">
          <h2 className="font-display text-base font-semibold">{RULES.readTitle}</h2>
          <p>{RULES.intro}</p>
          <ul className="list-disc space-y-1.5 pl-5">
            {RULES.points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{RULES.honest}</p>
        </CardContent>
      </Card>

      <Panel title={RULES.fullRules}>
        <p className="text-xs text-muted-foreground">Rules version {rules.data.version}</p>
        {rules.data.sections.map((s) => (
          <details key={s.id} className="rounded-xl border bg-card p-3">
            <summary className="min-h-6 cursor-pointer font-medium">{s.title}</summary>
            <div className="mt-2 space-y-2 text-sm">
              {s.paragraphs.map((p) => (
                <p key={p}>{p}</p>
              ))}
              {s.bullets?.length ? (
                s.ordered ? (
                  <ol className="list-decimal space-y-1 pl-5">
                    {s.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ol>
                ) : (
                  <ul className="list-disc space-y-1 pl-5">
                    {s.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                )
              ) : null}
            </div>
          </details>
        ))}
      </Panel>

      <Panel title={RULES.ticksTitle}>
        {rules.data.agreements.map((a) => (
          <label key={a.key} className="tap-row cursor-pointer" data-selected={ticks[a.key]}>
            <input
              type="checkbox"
              className="size-5 shrink-0"
              checked={ticks[a.key]}
              onChange={(e) => setTicks((t) => ({ ...t, [a.key]: e.target.checked }))}
            />
            <span>{a.label}</span>
          </label>
        ))}
        <OfflineNote online={online} />
        {me && !me.eligibility.eligible ? (
          <p role="alert" className="text-red-700">
            {friendlyError('grand_mock.not_eligible', '')}
          </p>
        ) : null}
        {problem ? (
          <p role="alert" className="text-red-700">
            {problem}
          </p>
        ) : null}
        {agreedAhead ? (
          <p role="status" className="rounded-xl border p-3">
            {RULES.agreedAhead}
          </p>
        ) : null}
        {!agreedAhead ? (
          <Button
            size="lg"
            className="w-full"
            disabled={disabled}
            {...pendingProps(busy, canStart)}
            onClick={() => void go()}
          >
            {goLabel(busy, canStart)}
          </Button>
        ) : null}
        <Button variant="outline" className="w-full" disabled={busy === 'start'} onClick={onBack}>
          {GM.back}
        </Button>
      </Panel>
    </div>
  );
}
